import { createSelector } from '@reduxjs/toolkit'
import type { Table } from 'apache-arrow'
import type { State } from '../../../../../Flux/Slice/index.ts'
import { cubeStateKey } from '../../../../../Flux/Slice/Visualization/VisualizationSlice.ts'
import type { CubeEntry } from '../../../../../Flux/Slice/Visualization/VisualizationSlice.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { getCubeTable } from '../../../../../Repository/Visualization/CubeTableRegistry.ts'
import { createSelectorCache, getOrBuildSelector } from './SelectorCache.ts'
import type { SampleBucket } from '../../../SampleGrouping/OperationTypes.ts'
import type { TransformResult } from '../../Transformer/Types.ts'

/**
 * Resolved per-source cube state, with the Arrow `Table` and the FE-side knobs that
 * downstream transformers always need. Returns one of four statuses; only the `ready`
 * variant carries a usable table.
 *
 * Centralised so every transformer (StackedBar, AreaChart, Map, Biodiversity, Set-math, …)
 * shares the same precondition logic instead of replicating the
 * `(scopeId, blastType, selectedBuckets, cubeEntry, table) → context` flow.
 *
 * The selector is keyed per `SourceId` and cached forever: createSelector's memoisation
 * gives a stable reference whenever the input slice hasn't changed, which is what makes
 * downstream `createSelector`s propagate cache hits.
 *
 */
export type CubeContext =
  | { status: 'idle' | 'loading' }
  | { status: 'error'; error: string }
  | {
      status: 'ready'
      table: Table
      selectedBuckets: SampleBucket[]
      countMode: 'READS' | 'UNIQUE'
      scalingMode: 'ABSOLUTE' | 'PERCENTAGE'
    }

const cubeContextSelectors = createSelectorCache<CubeContext>()

export function selectCubeContext(state: typeof State, sourceId: SourceId): CubeContext {
  return getOrBuildSelector(cubeContextSelectors, sourceId, () =>
    createSelector(
      [
        (s: typeof State) => s.visualization.scope?.id ?? null,
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.blastType ?? null,
        (s: typeof State) => s.visualization.sources[sourceId]?.snapshot?.selectedBuckets ?? null,
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.countMode ?? 'READS',
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.scalingMode ?? 'ABSOLUTE',
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.spin ?? null,
        (s: typeof State): CubeEntry | null => {
          const scopeId = s.visualization.scope?.id
          const blastType = s.visualization.sources[sourceId]?.shared?.blastType
          if (!scopeId || !blastType) return null
          const spin = s.visualization.sources[sourceId]?.shared?.spin
          return s.visualization.cubes[cubeStateKey(scopeId, blastType, spin)] ?? null
        }
      ],
      (scopeId, blastType, selectedBuckets, countMode, scalingMode, spin, cubeEntry): CubeContext => {
        if (!scopeId || !blastType || !selectedBuckets) return { status: 'idle' }
        if (!cubeEntry || cubeEntry.status === 'loading') return { status: 'loading' }
        if (cubeEntry.status === 'error') {
          return { status: 'error', error: cubeEntry.error ?? 'cube load failed' }
        }
        const table = getCubeTable(scopeId, blastType, spin ?? undefined)
        if (!table) return { status: 'loading' }
        return { status: 'ready', table, selectedBuckets, countMode, scalingMode }
      }
    )
  )(state)
}

/**
 * Map a non-ready [CubeContext] to a [TransformResult]. Used by every cube-backed
 * transformer to short-circuit when the cube isn't usable yet.
 */
export function forwardCubeStatus<T>(
  ctx: { status: 'idle' | 'loading' } | { status: 'error'; error: string }
): TransformResult<T> {
  if (ctx.status === 'error') return { status: 'error', error: ctx.error }
  return { status: ctx.status }
}
